import type { UserSummary } from '../../services/adminApi'

interface UserCardProps {
  user: UserSummary
  onClick?: (user: UserSummary) => void
}

const statusColors: Record<string, { bg: string; text: string; dot: string }> = {
  active: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-400' },
  suspended: { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-400' },
  banned: { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', dot: 'bg-red-400' },
  deleted: { bg: 'bg-gray-200 dark:bg-gray-700', text: 'text-gray-500 dark:text-gray-400', dot: 'bg-gray-400' },
}

export function UserCard({ user, onClick }: UserCardProps) {
  const formattedDate = new Date(user.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  const status = statusColors[user.status] || { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400' }
  const initials = (user.name || user.email).charAt(0).toUpperCase()

  // Check if user was recently active (within 4 hours, or 1 day if no time component)
  const isRecentlyActive = (() => {
    if (!user.last_active_at) return false
    const lastActive = new Date(user.last_active_at)
    const now = new Date()
    const diffMs = now.getTime() - lastActive.getTime()
    const diffHours = diffMs / (1000 * 60 * 60)
    // If timestamp has time component (not midnight), use 4 hour window
    const hasTimeComponent = lastActive.getHours() !== 0 || lastActive.getMinutes() !== 0
    return hasTimeComponent ? diffHours <= 4 : diffHours <= 24
  })()

  return (
    <div
      role="button"
      tabIndex={0}
      className="group relative rounded-2xl border border-[#ecdefc] dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] p-5 transition-all duration-300 cursor-pointer hover:shadow-md hover:-translate-y-1 shadow-sm dark:shadow-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#503864] dark:focus-visible:ring-[#b58fcc]"
      onClick={() => onClick?.(user)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.(user)
        }
      }}
    >
      <div className="relative flex items-start gap-4">
        {/* Avatar */}
        <div className="relative">
          <div className="h-14 w-14 flex-shrink-0 rounded-full bg-[#503864] dark:bg-[#7f4f9a] flex items-center justify-center ring-4 ring-white dark:ring-[#1a1520]">
            <span className="text-xl font-bold text-white">{initials}</span>
          </div>
          {/* Online indicator - only show if recently active */}
          {isRecentlyActive && (
            <span className="absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white dark:border-[#1a1520] bg-emerald-400" />
          )}
        </div>

        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-[#503864] dark:group-hover:text-[#b58fcc] transition-colors">
                {user.name || 'No name'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
            </div>
            <span className={`flex-shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${status.bg} ${status.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
              <span className="capitalize">{user.status}</span>
            </span>
          </div>

          <div className="mt-4 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
            <span className="inline-flex items-center gap-1.5">
              <svg className="w-4 h-4 text-[#7f4f9a] dark:text-[#b58fcc]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
              </svg>
              Joined {formattedDate}
            </span>
            {user.shop_id && (
              <span className="inline-flex items-center gap-1.5 text-[#503864] dark:text-[#b58fcc] font-medium">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
                </svg>
                Has Shop
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
